import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Search, Trash2, Upload } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"
import { RouteError } from "#/components/route-error"
import { SettingsSkeleton } from "#/components/skeletons/settings-skeleton"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "#/components/ui/alert-dialog"
import { Button } from "#/components/ui/button"
import { Card, CardContent } from "#/components/ui/card"
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "#/components/ui/dialog"
import { Input } from "#/components/ui/input"
import { Separator } from "#/components/ui/separator"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "#/components/ui/table"
import { useFeatureFlag } from "#/lib/feature-flags"
import { parseCsv } from "#/lib/parse-csv"
import { trackEvent } from "#/lib/posthog"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId/reference-data")({
	errorComponent: RouteError,
	component: ReferenceDataTab,
})

function ReferenceDataTab() {
	const { orgId } = Route.useParams()
	return (
		<section className="space-y-6">
			<div>
				<h3>Reference data</h3>
				<p className="text-sm text-foreground-muted">
					Maintain the parts and suppliers catalog used in exception forms.
				</p>
			</div>
			<div className="space-y-8">
				<PartsSection actingOrgId={orgId} />
				<Separator />
				<SuppliersSection actingOrgId={orgId} />
			</div>
		</section>
	)
}

function PartsSection({ actingOrgId }: { actingOrgId?: string }) {
	const parts = useQuery(api.stonelog.parts.list, { actingOrgId })
	const createPart = useMutation(api.stonelog.parts.create)
	const removePart = useMutation(api.stonelog.parts.remove)
	const batchCreateParts = useMutation(api.stonelog.parts.batchCreate)

	const [search, setSearch] = useState("")
	const [newNumber, setNewNumber] = useState("")
	const [newDesc, setNewDesc] = useState("")
	const [csvData, setCsvData] = useState<string[][] | null>(null)
	const [showCsvDialog, setShowCsvDialog] = useState(false)
	const [importResult, setImportResult] = useState<{
		created: number
		errors: string[]
	} | null>(null)
	const fileRef = useRef<HTMLInputElement>(null)
	const csvImportEnabled = useFeatureFlag("enable.csv-import")

	if (!parts) {
		return <SettingsSkeleton />
	}

	const filtered = search
		? parts.filter(
				(p) =>
					p.partNumber.toLowerCase().includes(search.toLowerCase()) ||
					p.description.toLowerCase().includes(search.toLowerCase()),
			)
		: parts

	const handleAdd = async () => {
		if (!newNumber.trim() || !newDesc.trim()) return
		try {
			const partId = await createPart({
				partNumber: newNumber.trim(),
				description: newDesc.trim(),
				actingOrgId,
			})
			trackEvent("stonelog_part_created", { part_id: partId })
			setNewNumber("")
			setNewDesc("")
			toast.success("Part created")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to create part")
		}
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = (ev) => {
			const text = ev.target?.result as string
			const rows = parseCsv(text)
			const data =
				rows.length > 0 && rows[0][0]?.toLowerCase().includes("part") ? rows.slice(1) : rows
			setCsvData(data)
			setShowCsvDialog(true)
			setImportResult(null)
		}
		reader.readAsText(file)
		e.target.value = ""
	}

	const handleImport = async () => {
		if (!csvData) return
		try {
			const items = csvData
				.filter((row) => row.length >= 2 && row[0] && row[1])
				.map((row) => ({ partNumber: row[0], description: row[1] }))
			const result = await batchCreateParts({ items, actingOrgId })
			trackEvent("stonelog_parts_batch_imported", { count: result.created })
			setImportResult(result)
			setCsvData(null)
			toast.success(`${result.created} parts imported`)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to import parts")
		}
	}

	return (
		<section>
			<h2 className="mb-4 text-lg font-medium">Parts</h2>

			<Card className="mb-4">
				<CardContent className="space-y-3 pt-4">
					<div className="flex items-center gap-2">
						<Input
							value={newNumber}
							onChange={(e) => setNewNumber(e.target.value)}
							placeholder="Part number"
							className="max-w-[200px]"
						/>
						<Input
							value={newDesc}
							onChange={(e) => setNewDesc(e.target.value)}
							placeholder="Description"
							className="max-w-xs"
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
						/>
						<Button onClick={handleAdd} disabled={!newNumber.trim() || !newDesc.trim()} size="sm">
							Add
						</Button>
					</div>
					<div className="flex items-center gap-2">
						<div className="relative max-w-xs flex-1">
							<Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search parts..."
								className="pl-8"
							/>
						</div>
						{csvImportEnabled && (
							<>
								<input
									ref={fileRef}
									type="file"
									accept=".csv"
									className="hidden"
									onChange={handleFileChange}
								/>
								<Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
									<Upload className="mr-1 size-4" />
									Import CSV
								</Button>
							</>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="pt-4">
					{filtered.length === 0 ? (
						<p className="py-8 text-center text-sm text-foreground-secondary">
							{parts.length === 0 ? "No parts yet." : "No parts match your search."}
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Part Number</TableHead>
									<TableHead>Description</TableHead>
									<TableHead className="w-[60px]" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{filtered.map((part) => (
									<TableRow key={part._id}>
										<TableCell className="font-medium">{part.partNumber}</TableCell>
										<TableCell className="text-foreground-secondary">{part.description}</TableCell>
										<TableCell>
											<AlertDialog>
												<AlertDialogTrigger
													render={<Button variant="destructive" size="icon-xs" />}
												>
													<Trash2 className="size-3" />
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>Delete Part</AlertDialogTitle>
														<AlertDialogDescription>
															Delete part &ldquo;{part.partNumber}&rdquo;?
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>Cancel</AlertDialogCancel>
														<AlertDialogAction
															onClick={async () => {
																try {
																	await removePart({ partId: part._id, actingOrgId })
																	trackEvent("stonelog_part_deleted", { part_id: part._id })
																	toast.success("Part deleted")
																} catch (err) {
																	Sentry.captureException(err)
																	toast.error(
																		err instanceof Error ? err.message : "Failed to delete part",
																	)
																}
															}}
														>
															Delete
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<Dialog open={showCsvDialog} onOpenChange={setShowCsvDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Import Parts from CSV</DialogTitle>
						<DialogDescription>
							{csvData ? `${csvData.length} rows found. Preview:` : ""}
						</DialogDescription>
					</DialogHeader>
					{csvData && (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Part Number</TableHead>
									<TableHead>Description</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{csvData.slice(0, 5).map((row, i) => (
									<TableRow key={row.join("-") || i}>
										<TableCell>{row[0]}</TableCell>
										<TableCell>{row[1]}</TableCell>
									</TableRow>
								))}
								{csvData.length > 5 && (
									<TableRow>
										<TableCell colSpan={2} className="text-center text-foreground-secondary">
											...and {csvData.length - 5} more rows
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
					{importResult && (
						<div className="text-sm">
							<p className="text-success">Created: {importResult.created}</p>
							{importResult.errors.length > 0 && (
								<p className="text-severe">Errors: {importResult.errors.length}</p>
							)}
						</div>
					)}
					<DialogFooter>
						<DialogClose render={<Button variant="outline">Cancel</Button>} />
						<Button onClick={handleImport} disabled={!csvData}>
							Import {csvData?.length ?? 0} rows
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</section>
	)
}

function SuppliersSection({ actingOrgId }: { actingOrgId?: string }) {
	const suppliers = useQuery(api.stonelog.suppliers.list, { actingOrgId })
	const createSupplier = useMutation(api.stonelog.suppliers.create)
	const removeSupplier = useMutation(api.stonelog.suppliers.remove)
	const batchCreateSuppliers = useMutation(api.stonelog.suppliers.batchCreate)

	const [search, setSearch] = useState("")
	const [newName, setNewName] = useState("")
	const [newNumber, setNewNumber] = useState("")
	const [csvData, setCsvData] = useState<string[][] | null>(null)
	const [showCsvDialog, setShowCsvDialog] = useState(false)
	const [importResult, setImportResult] = useState<{
		created: number
		errors: string[]
	} | null>(null)
	const fileRef = useRef<HTMLInputElement>(null)
	const csvImportEnabled = useFeatureFlag("enable.csv-import")

	if (!suppliers) {
		return <SettingsSkeleton />
	}

	const filtered = search
		? suppliers.filter(
				(s) =>
					s.name.toLowerCase().includes(search.toLowerCase()) ||
					s.supplierNumber?.toLowerCase().includes(search.toLowerCase()),
			)
		: suppliers

	const handleAdd = async () => {
		if (!newName.trim()) return
		try {
			const supplierId = await createSupplier({
				name: newName.trim(),
				supplierNumber: newNumber.trim() || undefined,
				actingOrgId,
			})
			trackEvent("stonelog_supplier_created", { supplier_id: supplierId })
			setNewName("")
			setNewNumber("")
			toast.success("Supplier created")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to create supplier")
		}
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = (ev) => {
			const text = ev.target?.result as string
			const rows = parseCsv(text)
			const data =
				rows.length > 0 && rows[0][0]?.toLowerCase().includes("name") ? rows.slice(1) : rows
			setCsvData(data)
			setShowCsvDialog(true)
			setImportResult(null)
		}
		reader.readAsText(file)
		e.target.value = ""
	}

	const handleImport = async () => {
		if (!csvData) return
		try {
			const items = csvData
				.filter((row) => row.length >= 1 && row[0])
				.map((row) => ({ name: row[0], supplierNumber: row[1] || undefined }))
			const result = await batchCreateSuppliers({ items, actingOrgId })
			trackEvent("stonelog_suppliers_batch_imported", { count: result.created })
			setImportResult(result)
			setCsvData(null)
			toast.success(`${result.created} suppliers imported`)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to import suppliers")
		}
	}

	return (
		<section>
			<h2 className="mb-4 text-lg font-medium">Suppliers</h2>

			<Card className="mb-4">
				<CardContent className="space-y-3 pt-4">
					<div className="flex items-center gap-2">
						<Input
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							placeholder="Supplier name"
							className="max-w-xs"
						/>
						<Input
							value={newNumber}
							onChange={(e) => setNewNumber(e.target.value)}
							placeholder="Number (optional)"
							className="max-w-[200px]"
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
						/>
						<Button onClick={handleAdd} disabled={!newName.trim()} size="sm">
							Add
						</Button>
					</div>
					<div className="flex items-center gap-2">
						<div className="relative max-w-xs flex-1">
							<Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search suppliers..."
								className="pl-8"
							/>
						</div>
						{csvImportEnabled && (
							<>
								<input
									ref={fileRef}
									type="file"
									accept=".csv"
									className="hidden"
									onChange={handleFileChange}
								/>
								<Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
									<Upload className="mr-1 size-4" />
									Import CSV
								</Button>
							</>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="pt-4">
					{filtered.length === 0 ? (
						<p className="py-8 text-center text-sm text-foreground-secondary">
							{suppliers.length === 0 ? "No suppliers yet." : "No suppliers match your search."}
						</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Number</TableHead>
									<TableHead className="w-[60px]" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{filtered.map((supplier) => (
									<TableRow key={supplier._id}>
										<TableCell className="font-medium">{supplier.name}</TableCell>
										<TableCell className="text-foreground-secondary">
											{supplier.supplierNumber ?? "—"}
										</TableCell>
										<TableCell>
											<AlertDialog>
												<AlertDialogTrigger
													render={<Button variant="destructive" size="icon-xs" />}
												>
													<Trash2 className="size-3" />
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>Delete Supplier</AlertDialogTitle>
														<AlertDialogDescription>
															Delete supplier &ldquo;{supplier.name}&rdquo;?
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>Cancel</AlertDialogCancel>
														<AlertDialogAction
															onClick={async () => {
																try {
																	await removeSupplier({
																		supplierId: supplier._id,
																		actingOrgId,
																	})
																	trackEvent("stonelog_supplier_deleted", {
																		supplier_id: supplier._id,
																	})
																	toast.success("Supplier deleted")
																} catch (err) {
																	Sentry.captureException(err)
																	toast.error(
																		err instanceof Error
																			? err.message
																			: "Failed to delete supplier",
																	)
																}
															}}
														>
															Delete
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<Dialog open={showCsvDialog} onOpenChange={setShowCsvDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Import Suppliers from CSV</DialogTitle>
						<DialogDescription>
							{csvData ? `${csvData.length} rows found. Preview:` : ""}
						</DialogDescription>
					</DialogHeader>
					{csvData && (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Number</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{csvData.slice(0, 5).map((row, i) => (
									<TableRow key={row.join("-") || i}>
										<TableCell>{row[0]}</TableCell>
										<TableCell>{row[1] ?? "—"}</TableCell>
									</TableRow>
								))}
								{csvData.length > 5 && (
									<TableRow>
										<TableCell colSpan={2} className="text-center text-foreground-secondary">
											...and {csvData.length - 5} more rows
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
					{importResult && (
						<div className="text-sm">
							<p className="text-success">Created: {importResult.created}</p>
							{importResult.errors.length > 0 && (
								<p className="text-severe">Errors: {importResult.errors.length}</p>
							)}
						</div>
					)}
					<DialogFooter>
						<DialogClose render={<Button variant="outline">Cancel</Button>} />
						<Button onClick={handleImport} disabled={!csvData}>
							Import {csvData?.length ?? 0} rows
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</section>
	)
}
